import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { HelpCircle, Info, Lightbulb } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * FieldTooltip - Dica flutuante para campos de formulário
 * 
 * Props:
 * - tip: string - Texto da dica
 * - type: 'help' | 'info' | 'tip' - Tipo de ícone
 * - side: 'top' | 'right' | 'bottom' | 'left' - Posição do tooltip
 * - className: string - Classes adicionais
 */
export const FieldTooltip = ({ 
  tip, 
  type = 'help',
  side = 'top',
  className 
}) => {
  const icons = {
    help: HelpCircle,
    info: Info,
    tip: Lightbulb,
  };
  
  const Icon = icons[type] || HelpCircle;
  
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex items-center justify-center w-4 h-4 text-slate-400 hover:text-orange-500 transition-colors",
              className
            )}
            onClick={(e) => e.preventDefault()}
          >
            <Icon className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side={side} 
          className="max-w-xs text-sm bg-slate-900 text-white border-0 px-3 py-2"
        >
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * LabelWithTooltip - Label com tooltip integrado
 */
export const LabelWithTooltip = ({ 
  children, 
  tip, 
  required = false,
  tipType = 'help',
  htmlFor,
  className 
}) => {
  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      <label 
        htmlFor={htmlFor}
        className="text-sm font-medium text-slate-700 dark:text-slate-300"
      >
        {children}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {tip && <FieldTooltip tip={tip} type={tipType} />}
    </div>
  );
};

/**
 * Dicas pré-definidas para campos comuns
 */
export const FIELD_TIPS = {
  // Roteiro
  itinerary: {
    title: 'Um título claro ajuda todos a entenderem a atividade rapidamente',
    category: 'Categorize para organizar melhor o roteiro por tipo de atividade',
    date: 'Selecione o dia da viagem em que esta atividade acontecerá',
    time: 'Defina horários para manter o grupo sincronizado',
    location: 'Nome do local (ex: Restaurante Bela Vista)',
    address: 'Endereço completo para facilitar a navegação',
    description: 'Adicione detalhes como o que levar, recomendações, etc.',
    cost: 'O custo total será dividido entre os participantes',
    participants: 'Escolha quem participará. Isso afeta a divisão de custos',
    payment: 'Ative para criar automaticamente um pagamento vinculado',
  },
  
  // Pagamentos
  payments: {
    title: 'Nome descritivo da despesa (ex: Hotel - 3 noites)',
    amount: 'Valor total que será dividido entre os participantes',
    category: 'Categorize para facilitar o controle de gastos',
    description: 'Adicione detalhes sobre a despesa, como observações ou instruções',
    dueDate: 'Data limite para pagamento',
    proof: 'Anexe comprovante PIX ou recibo para confirmar pagamento',
    participants: 'Selecione quem deve pagar. Cada um pagará sua parte',
  },
  
  // Caronas
  carpools: {
    departure: 'Endereço completo de onde sairá o carro',
    destination: 'Endereço de destino (geralmente o local da viagem)',
    departureTime: 'Horário de saída do ponto de encontro',
    seats: 'Quantas vagas disponíveis no seu carro',
    car: 'Modelo e cor do carro para facilitar identificação',
    fuelCost: 'Valor estimado de combustível para dividir entre passageiros',
    meetingPoint: 'Ponto de referência para encontrar os passageiros',
    notes: 'Informações extras: ar-condicionado, bagageiro, pets, etc.',
  },
  
  // Compras
  purchases: {
    name: 'Nome do item a ser comprado',
    quantity: 'Quantidade necessária',
    estimated: 'Valor aproximado para planejamento do orçamento',
    responsible: 'Quem ficará responsável por comprar este item',
    priority: 'Itens essenciais vs. opcionais',
    notes: 'Marca preferida, onde comprar, etc.',
  },
  
  // Checklist
  checklist: {
    task: 'Descreva a tarefa a ser realizada',
    responsible: 'Quem ficará responsável por esta tarefa',
    dueDate: 'Data limite para conclusão',
    category: 'Agrupe tarefas similares para melhor organização',
  },
  
  // Geral
  general: {
    invite: 'Compartilhe este código para convidar amigos para a viagem',
  },
};

export default FieldTooltip;
